import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type {
  ProviderPriceListItemOptionValueModel,
  ProviderPriceListItemOptionValueRequestOptions,
} from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['providerPriceListItemOptionValues']

type ProviderPriceListItemOptionValueFilters = {
  provider_price_list_item_option_id?: number,
  provider_price_list_id?: number,
  string?: string,
}

type UseProviderPriceListItemOptionValuesOptions = {
  filters?: ProviderPriceListItemOptionValueFilters,
  performHttpRequests?: boolean,
  requestOptions?: ProviderPriceListItemOptionValueRequestOptions,
  sortBy?: string,
}

function useProviderPriceListItemOptionValues(options: UseProviderPriceListItemOptionValuesOptions) {
  const { filters = {}, requestOptions, sortBy = 'price asc' } = options
  const {
    provider_price_list_item_option_id: filterProviderPriceListItemOptionId,
    provider_price_list_id: filterProviderPriceListId,
    string: filterString,
  } = filters

  const {
    updatedEntities: {
      providerPriceListItemOptionValues: providerPriceListItemOptionValuesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { providerPriceListItemOptionValues } = useSelector(reduxState => reduxState.entities)

  const filteredProviderPriceListItemOptionValues = useMemo(() => {
    const filtered = Object.values(providerPriceListItemOptionValues)
      .filter((providerPriceListItemOptionValue: ProviderPriceListItemOptionValueModel) => {
        const {
          id,
          provider_price_list_id,
          provider_price_list_item_option_id,
          title,
        } = providerPriceListItemOptionValue

        const matchId = id.toString() === filterString
        const matchTitle = matchFilterString(title, filterString)
        const matchString = matchId || matchTitle

        const matchProviderPriceListId = matchFilterNumber(provider_price_list_id, filterProviderPriceListId)

        const matchPriceListItemOptionId = matchFilterNumber(
          provider_price_list_item_option_id,
          filterProviderPriceListItemOptionId,
        )

        return matchPriceListItemOptionId && matchString && matchProviderPriceListId
      })

    const [column, direction] = sortBy.split(' ')

    if (sortBy === 'none'){
      return filtered
    }

    // @ts-expect-error TS2345
    return sortArrayBy(filtered, direction, column)
  }, [providerPriceListItemOptionValuesUpdatedAt, JSON.stringify(filters)])

  const filteredProviderPriceListItemOptionValuesCount = filteredProviderPriceListItemOptionValues.length
  const hasFilteredProviderPriceListItemOptionValues = !!filteredProviderPriceListItemOptionValuesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredProviderPriceListItemOptionValuesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProviderPriceListItemOptionValues } = useReduxAction(
    'providerPriceListItemOptionValues',
    'loadProviderPriceListItemOptionValues',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviderPriceListItemOptionValues,
    filteredProviderPriceListItemOptionValuesCount,
    hasFilteredProviderPriceListItemOptionValues,
    loading: loadingProviderPriceListItemOptionValues,
  }
}

export default useProviderPriceListItemOptionValues
